import type { MetricFormat } from "@/grid/core/types";

const compactNumber = new Intl.NumberFormat("en-CA", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const currencyCad = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
  maximumFractionDigits: 0,
});

const number = new Intl.NumberFormat("en-CA");

/** Grid cells: whole $250k, one decimal $1.2M. */
function formatCurrencyForCell(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  if (abs >= 1_000_000) {
    return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  }
  if (abs >= 10_000) {
    return `${sign}$${Math.round(abs / 1_000)}k`;
  }
  return currencyCad.format(value);
}

export function formatMetricValue(value: number | null | undefined, format: MetricFormat): string {
  if (value == null) return "—";

  switch (format) {
    case "currency":
      return currencyCad.format(value);
    case "compact":
      return compactNumber.format(value);
    case "number":
      return number.format(value);
  }

  return String(value);
}

/** Editable target fields — full currency with grouping, plain numbers otherwise. */
export function formatMetricForEditor(
  value: number | null | undefined,
  format: MetricFormat,
): string {
  if (value == null) return "";
  return formatMetricValue(value, format === "currency" ? "currency" : "number");
}

/** In-grid cell display — compact currency when values are large. */
export function formatMetricForCell(
  value: number | null | undefined,
  format: MetricFormat,
): string {
  if (value == null) return "—";

  if (format === "currency") {
    if (Math.abs(value) >= 10_000) return formatCurrencyForCell(value);
    return currencyCad.format(value);
  }

  if (format === "number") return formatMetricCompact(value);
  return formatMetricValue(value, format);
}

/** Short form for inside grid cells (e.g. 90345 → 90k). */
export function formatMetricCompact(value: number | null | undefined): string {
  if (value == null) return "—";
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}k`;
  return String(Math.round(value));
}
